<template>
    <div class="itemized-list-container">
        <div class="item-card bg-[linear-gradient(111.97deg,_#F4ECE1_0%,_#F5F3F0_100%)] rounded-2xl shadow-[0px_1.4px_2.8px_0px_#0000000D] mb-6 border border-[var(--bone)]" v-for="item in items" :key="item.tour_slot_id">
            <div class="item-header p-4">
                <div class="flex items-start justify-between gap-3">
                    <div class="flex-1">
                        <h4 class="item-title text-lg text-[var(--charcoal-dark)] font-medium">{{ item.package_name }}</h4>
                        <p class="item-datetime text-base text-[var(--tone-gray)]">{{ dateFormat(item.date) }} <span v-if="item.package_has_slots">@ {{ item.time_date }}</span></p>
                    </div>
                    <button 
                        class="edit-btn w-9 h-9 sm:w-10 sm:h-10 bg-[var(--charcoal)] rounded-lg flex items-center justify-center hover:bg-[var(--charcoal-dark)] transition-all transform hover:scale-105 flex-shrink-0" 
                        @click="editItem(item)" 
                        title="Edit Tour">
                        <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                    </button>
                </div>
            </div>

            <div v-if="Object.values(item.people_group).some(pax => pax > 0)" class="item-guests p-4 border-t border-[var(--bone)]">
                <div v-for="(pax, key) in item.people_group" :key="key">
                    <div v-if="pax > 0" class="guest-row flex items-center justify-between text-base text-[var(--charcoal-dark)]">
                        <span class="guest-label mr-1 font-bold">{{ item.rate_group[key] }}</span>
                        <span class="guest-value leading-none text-[var(--charcoal-dark)] bg-white px-2 py-1 mt-1 rounded">{{ pax }}</span>
                    </div>
                </div>
            </div>

            <div class="item-pricing p-4 border-t border-[var(--bone)]">
                <div class="pricing-line flex items-center justify-between text-base text-[var(--charcoal-dark)]">
                    <span class="mr-1 font-bold">Ticket Cost</span>
                    <span>{{ currencyFormat(item.subtotal) }}</span>
                </div>
                <div class="pricing-line flex items-center justify-between text-base text-[var(--charcoal-dark)]" v-if="item?.discount2_value > 0">
                    <span class="mr-1 font-bold">Discount <!-- <span v-if="item?.discount2_percentage">({{ item?.discount2_percentage }}%)</span> --></span>
                    <span class="discount">-{{ item?.discount2_value ? currencyFormat(item?.discount2_value) : currencyFormat(0) }}</span>
                </div>

                <div v-if="hasVisibleAddons(item?.custom_fields)" class="addons-section border-t border-b border-[var(--bone)] py-2 my-2">
                    <div class="addon-header text-base text-[var(--charcoal-dark)] font-bold">Add-ons:</div>
                    <div v-for="(option, idx) in item.custom_fields" :key="`custom-option-${idx}`">
                        <!-- Parent add-on (show if priced and has subtotal > 0) -->
                        <div v-if="option.priceInfo?.enabled && option.priceInfo?.subtotal > 0" class="pricing-line flex items-center justify-between text-sm text-[var(--charcoal-dark)]">
                            <span class="font-bold">{{ option.name }}</span>
                            <span>{{ currencyFormat(option.priceInfo.subtotal) }}</span>
                        </div>
                        
                        <!-- Child add-ons -->
                        <div v-if="option.children && option.children.length > 0">
                            <div v-for="(child, childIdx) in option.children" :key="`child-${idx}-${childIdx}`">
                                <div v-if="child.priceInfo?.enabled && child.priceInfo?.subtotal > 0" class="pricing-line child-addon flex items-center justify-between text-sm text-[var(--charcoal-dark)]">
                                    <span class="font-bold">  ↳ {{ child.name }}</span>
                                    <span>{{ currencyFormat(child.priceInfo.subtotal) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="pricing-line flex items-center justify-between text-base text-[var(--charcoal-dark)]">
                    <span class="mr-1 font-bold">Booking Fees</span>
                    <span>{{ currencyFormat(Number(item.fees) + Number(item.addons_fee)) }}</span>
                </div>
                <div class="pricing-line total flex items-center justify-between text-base text-[var(--charcoal-dark)]">
                    <span class="mr-1 font-bold">Tour Cost</span>
                    <span>{{ currencyFormat(Number(item.total) + Number(item.addons_total) + Number(item.addons_fee)) }}</span>
                </div>
            </div>

            <Discount :item="item" :allItem="items" :globalTotalItem="globalTotalItem"
                @update-items="handleItemsUpdate" />

            <div v-if="seatErrors?.length" class="errors-section">
                <p class="error-title">Error(s):</p>
                <ul class="error-list">
                    <li v-for="(error, index) in seatErrors" :key="index"
                        :class="{ 'error-success': error[item.tour_slot_id]?.success }">
                        <small>{{ error[item.tour_slot_id]?.message }}</small>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import { getMomentTimezone } from '../../utils/dateUtils';
import { formatCurrencyIntl } from '../../utils/currency';
import Discount from './Discount.vue';

export default {
    name: "ItemizedList",
    props: ["items", "globalTotalItem", "seatErrors"],
    emits: ["update-items", "edit-item"],
    components: {
        Discount,
    },
    data: () => {
        return {
        }
    },
    methods: {
        editItem(item) {
            this.$emit("edit-item", item);
        },
        dateFormat(date) {
            return getMomentTimezone(this.$store.state.timezone, date).format('dddd, MMMM D, YYYY');
        },
        currencyFormat(amount) {
            return formatCurrencyIntl(amount, this.$store.state.currency);
        },
        hasVisibleAddons(customFields) {
            if (!customFields || !Array.isArray(customFields)) return false;
            
            // Check if at least one add-on (parent or child) has price enabled and subtotal > 0
            return customFields.some(field => {
                // Check parent
                if (field.priceInfo?.enabled && field.priceInfo?.subtotal > 0) {
                    return true;
                }
                // Check children
                if (field.children && Array.isArray(field.children)) {
                    return field.children.some(child => 
                        child.priceInfo?.enabled && child.priceInfo?.subtotal > 0
                    );
                }
                return false;
            });
        },
        hasValidFieldValue(field) {
            // For custom_fields saved from AddonsNew, check if there's a valid subtotal
            // This handles all pricing types including price-per-pax and price-per-unit
            if (field.priceInfo && field.priceInfo.subtotal > 0) {
                return true;
            }
            
            // Fallback: check the value field
            const value = field.value;
            
            switch (field.type) {
                case 'checkbox':
                    return value === true;
                case 'number':
                    return Number(value) > 0;
                case 'radio':
                case 'select':
                    return value !== null && value !== undefined && value !== '';
                case 'text':
                case 'textarea':
                    return value !== null && value !== undefined && String(value).trim() !== '';
                default:
                    return value !== null && value !== undefined && value !== '';
            }
        },
        handleItemsUpdate(updatedItems) {
            // Emit the updated items list to `Checkout.vue`
            this.$emit("update-items", updatedItems);
        }
    }
}
</script>